import { Account } from '@citrite/sf-api';
import { PresetsByType, ShareSuccessReturnData } from '@sharefiledev/sharing-pilet-sdk';
import {
	PermissionConfigurationPostRequestLocaleEnum,
	Preset,
	Principal,
	PrincipalType,
	RelatedResource,
	ShareRequest,
	User,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { FormInstance } from 'antd';
import {
	getPreset,
	getPresetInfoTypeByPermission,
	getRidsForEntityType,
} from '../Links/ConditionalLinks/utils/linkShare-utils';
import { getRidsWithoutFileVersion } from '../Links/helpers/GetBaseRids';
import { IPermissionContext } from '../PermissionsContext/PluginContext';
import { PermissionsSelectionByType } from '../PermissionsContext/PluginContextProvider';
import { PermissionsHttp } from '../PermissionsHttp';
import { PermissionId } from '../ram-types';
import { getRelatedResourcesSharePayload } from '../RelatedResources/RelatedResources';
import { createPrincipal, getUserIDfromUserRID } from './principal-utils';

export type ValidationCallback = (error?: string | undefined) => void;

export const validateEmail = (
	emailValidate: (
		firstName: string,
		lastName: string,
		email: string
	) => string | undefined,
	firstName: string,
	lastName: string,
	email: string,
	callback: ValidationCallback
) => {
	const error = emailValidate(firstName, lastName, email);
	callback(error ?? undefined);
};

export const isValidUserInfo = (
	validate: any,
	form: FormInstance,
	t: (key: string) => string
): boolean => {
	const firstName = form.getFieldValue('firstName');
	const lastName = form.getFieldValue('lastName');
	const email = form.getFieldValue('email');

	if (!firstName || !lastName || !email) {
		return false;
	}

	let isValid = true;

	validateEmail(validate, firstName, lastName, email, (error?: string) => {
		if (error === t('createUser.emailExists')) {
			isValid = false;
		}
	});

	return isValid;
};

export const isShareDisabled = (
	context: IPermissionContext,
	form: FormInstance,
	isValidUserInfoFn: () => boolean
): boolean => {
	const userSelected = context.canQueryUsers
		? context.selectedPrincipals.length > 0
		: isValidUserInfoFn();

	const errorsExist =
		context.canQueryUsers &&
		!!form.getFieldsError().filter(({ errors }) => errors.length).length;

	return !userSelected || !form.isFieldTouched('selectedPermissions') || errorsExist;
};

/**
 * Executes the sharing of resources with principals.
 *
 * @param {string} [message] - Optional message to include with the share request.
 * @param {User[]} [newUsers] - Optional list of new users to share with. These are the users that needs to be created and does not have userId associated with them.
 * @param {RelatedResource[]} [relatedResourcesToShare] - Optional list of related resources to share along with the primary resources.
 * @returns {Promise<ShareSuccessReturnData>} - A promise that resolves to the data indicating the success of the share operation.
 *
 * @summary This function handles the sharing of resources with specified principals, including creating new users if necessary, and making the appropriate API calls to share the resources.
 */
export async function executeShare(
	message?: string,
	newUsersToCreate?: User[],
	relatedResourcesToShare?: RelatedResource[],
	selectedPrincipals?: Principal[],
	selectedPermissions?: PermissionId[],
	selectedPreset?: string,
	presets?: Preset[],
	stringRids?: string[],
	account?: Account,
	expirationDays?: number | null,
	eventId?: string
) {
	try {
		const createdNewPrincipals: Principal[] = [];

		if (newUsersToCreate) {
			createdNewPrincipals.push(
				...(await Promise.all(
					newUsersToCreate.map(user => createPrincipal(user, stringRids, account))
				))
			);
		}

		const principalsToCreateFromSelectedPrincipals = selectedPrincipals?.filter(
			principal => principal.type === PrincipalType.User && principal.rid === ''
		);

		if (principalsToCreateFromSelectedPrincipals?.length! > 0) {
			createdNewPrincipals.push(
				...(await Promise.all(
					(principalsToCreateFromSelectedPrincipals ?? []).map(principal =>
						createPrincipal(principal, stringRids, account)
					)
				))
			);
		}

		// Remove the principals without RID and add their resultant clientPost response to the context
		const filteredPrincipals = selectedPrincipals
			?.filter(principal => principal.rid !== null && principal.rid !== '')
			.concat(createdNewPrincipals);

		const principalRIDs = filteredPrincipals?.map(principal => principal.rid);

		const selectedPermissionsString = selectedPermissions?.map(permission =>
			permission.toString()
		);
		const shareRequestPayload: ShareRequest = {
			rids: getRidsWithoutFileVersion(stringRids ?? []),
			principalRIDs,
			permissions: selectedPermissionsString ?? [],
			notificationMessage: message,
			sendEmailNotification: true,
			presetRID: getPresetRID(presets, selectedPreset),
			expirationDays,
		};

		let relatedResourcesSharePayload: ShareRequest[] = [];

		// call bulk share endpoint if there are related resources to share along with primary resources otherwise share endpoint
		if (relatedResourcesToShare !== undefined && relatedResourcesToShare.length > 0) {
			relatedResourcesSharePayload = getRelatedResourcesSharePayload(
				principalRIDs ?? [],
				relatedResourcesToShare
			);
			const shareRequests = [shareRequestPayload, ...relatedResourcesSharePayload];
			await PermissionsHttp.bulkShare({ shareRequests });
		} else {
			await PermissionsHttp.share(shareRequestPayload);
		}

		const shareSuccessData: ShareSuccessReturnData = {
			userIds: principalRIDs?.map(getUserIDfromUserRID),
			principalRIDs,
			message,
			selectedPermissions: selectedPermissionsString,
			relatedRidsShared: relatedResourcesSharePayload.map(r => r.rids[0]),
			preset: presets?.find(preset => preset.name === selectedPreset),
			eventId,
		};
		return shareSuccessData;
	} catch (error) {
		return null;
	}
}

export async function executeShareByConfigurationType(
	message?: string,
	newUsersToCreate?: User[],
	relatedResourcesToShare?: RelatedResource[],
	selectedPrincipals?: Principal[],
	selectedPermissions?: PermissionsSelectionByType,
	selectedPreset?: string,
	presets?: PresetsByType[],
	stringRids?: string[],
	account?: Account,
	expirationDays?: number | null,
	eventId?: string
) {
	try {
		if (!selectedPermissions || !presets) {
			return null;
		}
		const createdNewPrincipals: Principal[] = [];

		if (newUsersToCreate) {
			createdNewPrincipals.push(
				...(await Promise.all(
					newUsersToCreate.map(user => createPrincipal(user, stringRids, account))
				))
			);
		}

		const principalsToCreateFromSelectedPrincipals = selectedPrincipals?.filter(
			principal => principal.type === PrincipalType.User && principal.rid === ''
		);

		if (principalsToCreateFromSelectedPrincipals?.length! > 0) {
			createdNewPrincipals.push(
				...(await Promise.all(
					(principalsToCreateFromSelectedPrincipals ?? []).map(principal =>
						createPrincipal(principal, stringRids, account)
					)
				))
			);
		}

		// Remove the principals without RID and add their resultant clientPost response to the context
		const filteredPrincipals = selectedPrincipals
			?.filter(principal => principal.rid !== null && principal.rid !== '')
			.concat(createdNewPrincipals);

		const principalRIDs = filteredPrincipals?.map(principal => principal.rid);
		const rids = getRidsWithoutFileVersion(stringRids ?? []);

		const keys: string[] = Object.keys(selectedPermissions ?? {});
		const shareRequestPayload: ShareRequest[] = keys
			.filter(resource => selectedPermissions[resource].length > 0)
			.map(resource => ({
				rids: getRidsForEntityType(resource, rids),
				principalRIDs,
				permissions: selectedPermissions[resource].map(p => p.toString()) ?? [],
				notificationMessage: message,
				sendEmailNotification: true,
				presetRID: getPresetInfoTypeByPermission(selectedPreset, presets)?.rid,
				expirationDays,
			}));

		let relatedResourcesSharePayload: ShareRequest[] = [];

		// call bulk share endpoint if there are related resources to share along with primary resources otherwise share endpoint
		if (
			(relatedResourcesToShare !== undefined && relatedResourcesToShare.length > 0) ||
			shareRequestPayload.length > 1
		) {
			relatedResourcesSharePayload = getRelatedResourcesSharePayload(
				principalRIDs ?? [],
				relatedResourcesToShare!
			);
		}
		const shareRequests: ShareRequest[] = [
			...shareRequestPayload,
			...relatedResourcesSharePayload,
		];
		await PermissionsHttp.bulkShare({ shareRequests });

		const shareSuccessData: ShareSuccessReturnData[] = shareRequests.map(
			(shareRequest, index) => ({
				userIds: principalRIDs?.map(getUserIDfromUserRID),
				principalRIDs,
				message: shareRequest.notificationMessage ?? undefined,
				selectedPermissions: shareRequest.permissions,
				relatedRidsShared: relatedResourcesSharePayload
					.filter(r => shareRequest.rids.includes(r.rids[0]))
					.map(r => r.rids[0]),
				preset: getPreset(presets, keys[index])?.find(
					preset => preset.name === selectedPreset
				),
				eventId,
			})
		);
		return shareSuccessData;
	} catch (error) {
		return null;
	}
}

export const getPresetRID = (presets?: Preset[], presetName?: string) =>
	presetName ? presets?.find(p => p.name === presetName)?.rid : undefined;

const languageMap: Record<string, PermissionConfigurationPostRequestLocaleEnum> = {
	en: PermissionConfigurationPostRequestLocaleEnum.En,
	de: PermissionConfigurationPostRequestLocaleEnum.De,
	es: PermissionConfigurationPostRequestLocaleEnum.Es,
	fr: PermissionConfigurationPostRequestLocaleEnum.Fr,
	it: PermissionConfigurationPostRequestLocaleEnum.It,
	ja: PermissionConfigurationPostRequestLocaleEnum.Ja,
	nl: PermissionConfigurationPostRequestLocaleEnum.Nl,
	'pt-BR': PermissionConfigurationPostRequestLocaleEnum.PtBr,
};

export const mapLanguageToEnum = (
	language: string
): PermissionConfigurationPostRequestLocaleEnum => {
	const mappedValue = languageMap[language];
	if (!mappedValue) {
		throw new Error(`Unsupported language: ${language}`);
	}
	return mappedValue;
};
